/**
 * Tax Automation
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/tax-automation/
 * Wired to GET /api/v1/tax-automation/summary and /deductible-transactions
 * (personal-scoped — reads the same PersonalTransaction records Finance.tsx manages).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { taxAutomationApi, type PersonalTransactionItem } from '../services/api';

interface TaxSummary {
  taxYear: number;
  totalDeductions: number;
  deductionsByType: Record<string, number>;
  totalGrossIncome: number;
  totalNetIncome: number;
  estimatedTaxableIncome: number;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

const TaxAutomation: React.FC = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [summary, setSummary] = useState<TaxSummary | null>(null);
  const [transactions, setTransactions] = useState<PersonalTransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [summaryRes, txRes] = await Promise.all([
        taxAutomationApi.summary(year),
        taxAutomationApi.deductibleTransactions(year),
      ]);
      if (summaryRes?.success) setSummary(summaryRes?.data ?? null);
      else setError(summaryRes?.error || 'Failed to load tax summary');

      if (txRes?.success) setTransactions(Array.isArray(txRes?.data) ? txRes.data : []);
      else setError((prev) => prev || txRes?.error || 'Failed to load deductible transactions');
    } catch {
      setError('Failed to load tax data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const deductionEntries = summary ? Object.entries(summary.deductionsByType) : [];

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🧾 Tax Automation</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              One-click summary of your deductions, income, and estimated taxable income
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', fontSize: '14px' }}
            >
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y} style={{ color: '#333' }}>{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading…</div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}

      {!loading && summary && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>{summary.taxYear} summary</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>Total deductions</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#28a745' }}>{formatCurrency(summary.totalDeductions)}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>Gross income</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{formatCurrency(summary.totalGrossIncome)}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>Net income</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{formatCurrency(summary.totalNetIncome)}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>Estimated taxable income</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc3545' }}>{formatCurrency(summary.estimatedTaxableIncome)}</div>
            </div>
          </div>

          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#333' }}>Deductions by type</h3>
          {deductionEntries.length === 0 ? (
            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>No deductions recorded for {summary.taxYear}.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {deductionEntries.map(([type, amount]) => (
                <li
                  key={type}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                  }}
                >
                  <span>{type}</span>
                  <strong>{formatCurrency(amount)}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>Deductible transactions</h2>
          {transactions.length === 0 ? (
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
              No deductible transactions for {year}.{' '}
              <Link to="/finance" style={{ color: '#667eea' }}>Mark transactions as tax deductible in Finance.</Link>
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {transactions.map((t) => (
                <li
                  key={t.id}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div>
                    <strong>{t.deductionType || t.category}</strong>
                    {t.businessName && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>{t.businessName}</span>
                    )}
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                      {new Date(t.transactionDate).toLocaleDateString()}
                    </p>
                  </div>
                  <strong style={{ fontSize: '15px', color: '#333' }}>{formatCurrency(t.amount)}</strong>
                </li>
              ))}
            </ul>
          )}
          <p style={{ margin: '16px 0 0 0', fontSize: '12px', color: '#999' }}>
            <Link to="/finance" style={{ color: '#667eea' }}>Open Finance</Link> to add or edit transactions.
          </p>
        </div>
      )}
    </div>
  );
};

export default TaxAutomation;
